import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface TruncatedTextProps {
  /** The full text — rendered truncated, and shown in full via tooltip when it
   *  actually overflows its box. */
  text: string;
  /** Element to render as (default `span`). Use `label` for form-control labels;
   *  pass `htmlFor` through `elementProps` in that case. */
  as?: "span" | "label";
  className?: string;
  /** How many lines to show before the ellipsis; defaults to 1. At 2 the text
   *  wraps (breaking an unbroken token rather than overflowing) and clamps on the
   *  second line, and "clipped" means it ran past that line. */
  lines?: 1 | 2;
  /** Tooltip side; defaults to `top`. */
  side?: "top" | "bottom" | "left" | "right";
  /** Extra attributes forwarded to the text element (e.g. `htmlFor` for a label). */
  elementProps?: React.HTMLAttributes<HTMLElement> & { htmlFor?: string };
  /** Optional decorated rendering of the same string — e.g. a search-highlighted
   *  `<mark>` wrapper. `text` still drives measurement and the tooltip, so the
   *  decoration must not change the rendered characters. */
  children?: React.ReactNode;
}

/**
 * Text that truncates with an ellipsis (on one line, or the second of two) and reveals the full value in
 * a tooltip **only when it is actually clipped** — so short labels don't sprout a
 * noisy tooltip. Replaces the native `title` attribute (unstyled, inconsistent,
 * and not keyboard/touch-friendly) with the brand `Tooltip` primitive.
 *
 * Overflow is detected by comparing `scrollWidth`/`clientWidth` on the text node
 * (`scrollHeight`/`clientHeight` when clamped to two lines),
 * re-measured on mount, whenever `text` changes, and on resize (the container may
 * be responsive), via a ResizeObserver.
 */
export const TruncatedText = observer(function TruncatedText({
  text,
  as = "span",
  className,
  lines = 1,
  side = "top",
  elementProps,
  children,
}: TruncatedTextProps) {
  const ref = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () =>
      setIsTruncated(
        lines === 1 ? el.scrollWidth > el.clientWidth : el.scrollHeight > el.clientHeight,
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // `isTruncated` is a dep so the observer re-attaches to the CURRENT node after
    // the plain↔tooltip-wrapped element swaps (they are different DOM nodes). The
    // measure is idempotent — same widths → same state — so this can't loop.
  }, [text, isTruncated, lines]);

  const Element = as;
  const textClassName = cn(
    lines === 1 ? "block truncate" : "line-clamp-2 wrap-anywhere",
    className,
  );
  const display = children ?? text;

  // Not clipped → plain text, no tooltip machinery.
  if (!isTruncated) {
    return (
      <Element ref={ref as never} className={textClassName} {...elementProps}>
        {display}
      </Element>
    );
  }

  // Clipped → wrap in the brand tooltip. base-ui's TooltipTrigger takes a BARE
  // element via `render` and the visible content as CHILDREN (it merges its hover
  // props + children onto the render element) — passing an element that already
  // has children as `render` does NOT wire up the hover handlers, so the tooltip
  // never opens. The render element is the truncating box, so it carries the ref
  // (for measuring), the truncate classes, and any element props (e.g. htmlFor).
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={<Element ref={ref as never} className={textClassName} {...elementProps} />}
        >
          {display}
        </TooltipTrigger>
        <TooltipContent side={side}>{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});
